import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/InputDefault";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { GraduationCap, Plus, Edit, Trash2, Loader2, GripVertical } from "lucide-react";
import apiService from "@/lib/api-functions/api-service";
import { hasPermission } from "@/lib/utils/permissions";
import type { TutorialTask } from "@/lib/stores/storeTypes";
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type TutorialManagementSectionProps = Record<string, never>;

// Available task types
const TASK_TYPES = [
  { value: 'create_glossary_item', label: 'Create Glossary Item' },
  { value: 'add_string', label: 'Add String' },
  { value: 'create_document', label: 'Create Document' },
  { value: 'add_translation', label: 'Add Translation' },
  { value: 'create_type', label: 'Create Type' },
  { value: 'create_tag', label: 'Create Tag' },
  { value: 'invite_user', label: 'Invite User' },
  { value: 'export_data', label: 'Export Data' },
];

export default function TutorialManagementSection(_props: TutorialManagementSectionProps) {
  const { t } = usePlatformTranslation();
  const { toast } = useToast();
  const { selectedCompany } = useSimplifiedUserData();
  
  const [tasks, setTasks] = useState<TutorialTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTask, setEditingTask] = useState<TutorialTask | null>(null);
  const [deleteConfirmTask, setDeleteConfirmTask] = useState<TutorialTask | null>(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    task_type: 'create_glossary_item',
    role_applicability: [] as string[],
    order_index: 0,
    is_active: true,
  });

  // Check if user has permission to manage tutorials
  const canManageTutorials = selectedCompany ? hasPermission(selectedCompany.id, 'roles.manage') : false;

  // Load tasks
  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getTutorialTasks();
      
      if (response.success && response.data) {
        // Sort by order_index
        const sortedTasks = response.data.sort((a: TutorialTask, b: TutorialTask) => 
          a.order_index - b.order_index
        );
        setTasks(sortedTasks);
      }
    } catch (error) {
      console.error("Failed to load tutorial tasks:", error);
      toast({
        title: "Error",
        description: "Failed to load tutorial tasks",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (canManageTutorials) {
      loadTasks();
    }
  }, [canManageTutorials, loadTasks]);

  const handleCreateTask = async () => {
    if (!formData.title || !formData.description) {
      toast({
        title: "Validation Error",
        description: "Title and description are required",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.createTutorialTask({
        ...formData,
        order_index: tasks.length,
      });

      if (response.success) {
        toast({
          title: "Success",
          description: "Tutorial task created successfully",
        });
        setShowCreateForm(false);
        resetForm();
        loadTasks();
      } else {
        throw new Error(response.error || 'Failed to create task');
      }
    } catch (error) {
      console.error("Failed to create tutorial task:", error);
      toast({
        title: "Error",
        description: "Failed to create tutorial task",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTask = async () => {
    if (!editingTask) return;

    if (!formData.title || !formData.description) {
      toast({
        title: "Validation Error",
        description: "Title and description are required",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.updateTutorialTask(editingTask.id, formData);

      if (response.success) {
        toast({
          title: "Success",
          description: "Tutorial task updated successfully",
        });
        setEditingTask(null);
        resetForm();
        loadTasks();
      } else {
        throw new Error(response.error || 'Failed to update task');
      }
    } catch (error) {
      console.error("Failed to update tutorial task:", error);
      toast({
        title: "Error",
        description: "Failed to update tutorial task",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTask = async (task: TutorialTask) => {
    setLoading(true);
    try {
      const response = await apiService.deleteTutorialTask(task.id);

      if (response.success) {
        toast({
          title: "Success",
          description: "Tutorial task deleted successfully",
        });
        setDeleteConfirmTask(null);
        loadTasks();
      } else {
        throw new Error(response.error || 'Failed to delete task');
      }
    } catch (error) {
      console.error("Failed to delete tutorial task:", error);
      toast({
        title: "Error",
        description: "Failed to delete tutorial task",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (task: TutorialTask) => {
    setLoading(true);
    try {
      const response = await apiService.updateTutorialTask(task.id, {
        is_active: !task.is_active
      });

      if (response.success) {
        toast({
          title: "Success",
          description: `Task ${task.is_active ? 'deactivated' : 'activated'}`,
        });
        loadTasks();
      }
    } catch (error) {
      console.error("Failed to toggle task:", error);
      toast({
        title: "Error",
        description: "Failed to update task",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      task_type: 'create_glossary_item',
      role_applicability: [],
      order_index: 0,
      is_active: true,
    });
  };

  const startEdit = (task: TutorialTask) => {
    setFormData({
      title: task.title,
      description: task.description,
      task_type: task.task_type,
      role_applicability: task.role_applicability || [],
      order_index: task.order_index,
      is_active: task.is_active,
    });
    setEditingTask(task);
    setShowCreateForm(true);
  };

  const cancelEdit = () => {
    setEditingTask(null);
    setShowCreateForm(false);
    resetForm();
  };

  if (!canManageTutorials) {
    return (
      <div className="text-center p-8">
        <p className="text-muted-foreground">
          You don't have permission to manage tutorial tasks.
        </p>
      </div>
    );
  }

  if (loading && tasks.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2 text-center">
            <GraduationCap className="h-6 w-6" />
            Tutorial Management
          </h2>
          <p className="text-muted-foreground text-center">
            Create and manage interactive tutorial tasks for users
          </p>
        </div>
        {!showCreateForm && (
          <Button onClick={() => setShowCreateForm(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Task
          </Button>
        )}
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-center">{editingTask ? 'Edit Task' : 'Create New Task'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="title">{t('platform.settings.tutorialmanagementsection.title')}</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={t('platform.settings.tutorialmanagementsection.eg_create_your_first_glossary_item')}
              />
            </div>

            <div>
              <Label htmlFor="description">{t('platform.settings.tutorialmanagementsection.description')}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('platform.settings.tutorialmanagementsection.detailed_instructions_for_completing_this_task')}
                rows={4}
              />
            </div>

            <div>
              <Label htmlFor="task_type">{t('platform.settings.tutorialmanagementsection.task_type')}</Label>
              <select
                id="task_type"
                value={formData.task_type}
                onChange={(e) => setFormData({ ...formData, task_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-md bg-background"
              >
                {TASK_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="roles">{t('platform.settings.tutorialmanagementsection.role_applicability_leave_empty_for_all_roles')}</Label>
              <Input
                id="roles"
                value={formData.role_applicability.join(', ')}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  role_applicability: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                })}
                placeholder={t('platform.settings.tutorialmanagementsection.eg_owner_admin_comma_separated')}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="is_active">{t('platform.settings.tutorialmanagementsection.active')}</Label>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={cancelEdit}>
                Cancel
              </Button>
              <Button onClick={editingTask ? handleUpdateTask : handleCreateTask} disabled={loading}>
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingTask ? 'Update Task' : 'Create Task'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Task List */}
      <div className="space-y-3">
        {tasks.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <GraduationCap className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                No tutorial tasks yet. Create your first task to get started.
              </p>
            </CardContent>
          </Card>
        ) : (
          tasks.map((task, index) => (
            <Card key={task.id} className={!task.is_active ? 'opacity-60' : ''}>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="mt-1">
                      <GripVertical className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <CardTitle className="text-lg text-center">
                          {index + 1}. {task.title}
                        </CardTitle>
                        {!task.is_active && (
                          <Badge variant="secondary">{t('platform.settings.tutorialmanagementsection.inactive')}</Badge>
                        )}
                      </div>
                      <CardDescription className="text-center">{task.description}</CardDescription>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="outline">{TASK_TYPES.find(t => t.value === task.task_type)?.label || task.task_type}</Badge>
                        {task.role_applicability && task.role_applicability.length > 0 ? (
                          task.role_applicability.map(role => (
                            <Badge key={role} variant="secondary">{role}</Badge>
                          ))
                        ) : (
                          <Badge variant="secondary">{t('platform.settings.tutorialmanagementsection.all_roles')}</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={task.is_active}
                      onCheckedChange={() => handleToggleActive(task)}
                      disabled={loading}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => startEdit(task)}
                      disabled={loading}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteConfirmTask(task)}
                      disabled={loading}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmTask} onOpenChange={() => setDeleteConfirmTask(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-center">{t('platform.settings.tutorialmanagementsection.delete_tutorial_task')}</AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              Are you sure you want to delete "{deleteConfirmTask?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('platform.common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirmTask && handleDeleteTask(deleteConfirmTask)}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

